import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm } from 'redux-form';
import { Button } from 'reactstrap';
import { connect } from 'react-redux';
import EyeIcon from 'mdi-react/EyeIcon';
import LockIcon from '@mui/icons-material/Lock';
import SaveIcon from '@mui/icons-material/Save';
import PersonIcon from '@mui/icons-material/Person';

// Custom validation function for the form
const validate = values => {
  const errors = {};
  
  if (!values.password) {
    errors.password = 'Required';
  } else {
    const uppercaseRegExp = /(?=.*?[A-Z])/;
    const lowercaseRegExp = /(?=.*?[a-z])/;
    const digitsRegExp = /(?=.*?[0-9])/;
    const specialCharRegExp = /(?=.*?[#?!@$%^&*-])/;
    const minLengthRegExp = /.{8,}/;

    if (!uppercaseRegExp.test(values.password) || 
        !lowercaseRegExp.test(values.password) || 
        !digitsRegExp.test(values.password) || 
        !specialCharRegExp.test(values.password) || 
        !minLengthRegExp.test(values.password)) {
      errors.password = 'Password must include at least one lowercase letter, one uppercase letter, one special character, one digit and must be at least 8 characters long.';
    }
  }

  if (!values.confirmPassword) {
    errors.confirmPassword = 'Required';
  } else if (values.password !== values.confirmPassword) {
    errors.confirmPassword = 'Password must match';
  }

  return errors;
};

// Custom modern field renderer
const RenderCompactField = ({ input, placeholder, type, meta: { touched, error }, disabled, icon: Icon, children }) => {
  const [focused, setFocused] = useState(false);

  return (
    <div className="mb-3">
      <label className="form-label fw-bold text-muted mb-1" style={{ fontSize: '0.75rem', letterSpacing: '0.3px' }}>
        {placeholder} <span className="text-danger">*</span>
      </label>
      <div 
        className="d-flex align-items-center"
        style={{
          border: touched && error ? '1px solid #ef4444' : (focused ? '1px solid #0f766e' : '1px solid #cbd5e1'),
          borderRadius: '8px',
          backgroundColor: '#fff',
          transition: 'all 0.2s ease-in-out',
          boxShadow: focused ? '0 0 0 3px rgba(15, 118, 110, 0.15)' : 'none',
          overflow: 'hidden'
        }}
      >
        <span 
          className="d-flex align-items-center justify-content-center text-muted" 
          style={{ 
            padding: '0.5rem 0.75rem',
            backgroundColor: '#f8fafc',
            borderRight: '1px solid #e2e8f0',
            minWidth: '40px'
          }}
        >
          <Icon style={{ fontSize: '1.1rem', color: focused ? '#0f766e' : '#64748b' }} />
        </span>
        <input
          {...input}
          className="w-100 border-0 px-3 py-2"
          placeholder={placeholder}
          type={type}
          disabled={disabled}
          onFocus={() => setFocused(true)}
          onBlur={(e) => {
            setFocused(false);
            if (input.onBlur) input.onBlur(e);
          }}
          style={{ 
            fontSize: '0.875rem', 
            outline: 'none',
            color: '#1e293b',
            backgroundColor: 'transparent'
          }}
        />
        {children}
      </div>
      {touched && error && (
        <div className="text-danger mt-1 small d-flex align-items-start" style={{ fontSize: '0.75rem', fontWeight: 500, lineHeight: '1.3' }}>
          <span className="me-1">⚠️</span>
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

const UpdateMerchantResetPassword = ({ handleSubmit, userData }) => {
  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);

  const showPasswordToggle1 = () => {
    setShowPassword1(!showPassword1);
  };

  const showPasswordToggle2 = () => {
    setShowPassword2(!showPassword2);
  };

  return (
    <form className="form-animated" onSubmit={handleSubmit}>
      {/* Merchant Info Section - Displayed as a premium profile card */}
      <div 
        className="mb-4 p-3" 
        style={{ 
          backgroundColor: '#f8fafc', 
          borderLeft: '4px solid #0f766e',
          borderRadius: '4px 8px 8px 4px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
        }}
      >
        <div className="d-flex align-items-center mb-3">
          <PersonIcon style={{ fontSize: '1.1rem', color: '#0f766e' }} className="me-2" />
          <span className="text-dark fw-bold small text-uppercase" style={{ letterSpacing: '0.5px', fontSize: '0.75rem' }}>
            Merchant User Details
          </span>
        </div>
        <div className="d-flex align-items-center" style={{ gap: '2.5rem' }}>
          <div>
            <span className="text-muted d-block small mb-1" style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
              Merchant Name
            </span>
            <span className="text-dark fw-semibold" style={{ fontSize: '0.9rem' }}>
              {userData?.merchantName || "—"}
            </span>
          </div>
          <div style={{ borderLeft: '1px solid #e2e8f0', height: '32px' }}></div>
          <div>
            <span className="text-muted d-block small mb-1" style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
              Username
            </span>
            <span className="text-dark fw-semibold" style={{ fontSize: '0.9rem' }}>
              {userData?.userName || "—"}
            </span>
          </div>
        </div>
      </div>

      {/* New Password Section */}
      <div>
        <div className="d-flex align-items-center mb-3">
          <LockIcon style={{ fontSize: '1.1rem', color: '#0f766e' }} className="me-2" />
          <span className="text-dark fw-bold small text-uppercase" style={{ letterSpacing: '0.5px', fontSize: '0.75rem' }}>
            Set New Password
          </span>
        </div>
        
        {/* Vertically stacked inputs for proper space */}
        <div className="mb-2">
          <Field
            name="password"
            component={RenderCompactField}
            type={showPassword1 ? 'text' : 'password'}
            placeholder="New Password"
            icon={LockIcon}
          >
            <button
              type="button"
              className="btn border-0 d-flex align-items-center justify-content-center"
              onClick={showPasswordToggle1}
              style={{ 
                padding: '0.5rem 0.75rem', 
                backgroundColor: '#f8fafc',
                borderLeft: '1px solid #e2e8f0',
                color: '#64748b',
                borderRadius: 0,
                transition: 'color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#0f766e'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#64748b'}
            >
              <EyeIcon size={14} />
            </button>
          </Field>
        </div>

        <div className="mb-2">
          <Field
            name="confirmPassword"
            component={RenderCompactField}
            type={showPassword2 ? 'text' : 'password'}
            placeholder="Confirm Password"
            icon={LockIcon}
          >
            <button
              type="button"
              className="btn border-0 d-flex align-items-center justify-content-center"
              onClick={showPasswordToggle2}
              style={{ 
                padding: '0.5rem 0.75rem', 
                backgroundColor: '#f8fafc',
                borderLeft: '1px solid #e2e8f0',
                color: '#64748b',
                borderRadius: 0,
                transition: 'color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#0f766e'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#64748b'}
            >
              <EyeIcon size={14} />
            </button>
          </Field>
        </div>
      </div>

      {/* Button footer */}
      <div className="d-flex justify-content-end mt-4 pt-3 border-top" style={{ borderColor: '#e2e8f0' }}>
        <Button
          color="primary"
          type="submit"
          className="px-4 py-2 d-flex align-items-center justify-content-center"
          style={{ 
            background: 'linear-gradient(135deg, #0f766e 0%, #134e4a 100%)', 
            border: 'none', 
            color: '#fff', 
            fontWeight: '600', 
            fontSize: '0.85rem',
            borderRadius: '8px',
            letterSpacing: '0.3px',
            transition: 'all 0.2s ease-in-out',
            boxShadow: '0 4px 6px -1px rgba(15, 118, 110, 0.2), 0 2px 4px -1px rgba(15, 118, 110, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 6px 12px -2px rgba(15, 118, 110, 0.3), 0 3px 6px -2px rgba(15, 118, 110, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'none';
            e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(15, 118, 110, 0.2), 0 2px 4px -1px rgba(15, 118, 110, 0.1)';
          }}
        >
          <SaveIcon style={{ fontSize: '1.1rem' }} className="me-2" />
          Update Password
        </Button>
      </div>
    </form>
  );
};

UpdateMerchantResetPassword.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  userData: PropTypes.object,
};

const mapStateToProps = (state, data) => {
  if (data && data.userData) {
    return {
      initialValues: {
        userId: data.userData?.merchantUserId,
        userName: data.userData.userName,
        merchantName: data.userData.merchantName,
      }
    };
  }
};

export default connect(mapStateToProps)(reduxForm({ form: 'UpdateMerchantResetPasswordForm', validate, enableReinitialize: true })(UpdateMerchantResetPassword));
